/**
 * Curated architecture model of the current Yango product, derived from
 * the 8 meeting products in agent-os/meetings/ (May 05 – May 12, 2026).
 *
 * Only systems that exist today are listed here. Speculative replacements
 * (new B2B PIM, CRM Platform, voucher system, etc.) belong on the future
 * roadmap views, not this diagram.
 *
 * Domains: 8 unified domains shared with the roadmap boards via
 * `presentation/data/domains.ts`. Layout: components are grouped into
 * coloured clusters placed in a 3×3 meta-grid with a wide bottom row.
 */

import { type Domain, DOMAIN_COLOR, DOMAIN_COLOR_SOFT, DOMAIN_LABEL } from "./domains";

export { DOMAIN_LABEL, DOMAIN_COLOR, DOMAIN_COLOR_SOFT };
export type { Domain };

export const STAGES = ["b2c", "b2b", "mvp", "b2b-users"] as const;
export type Stage = (typeof STAGES)[number];

/** Stages that source nodes/edges from the runtime workspace JSONB (editable). */
export const EDITABLE_STAGES = new Set<Stage>(["b2b-users"]);

export type Ownership = "yandex" | "external";

export type ArchitectureComponent = {
  id: string;
  name: string;
  role: string;
  ownership: Ownership;
  domain: Domain;
  stages: readonly Stage[];
  meetings: readonly string[];
  position: { x: number; y: number };
  note?: string;
  /**
   * Component importance for candidate composite scoring (1..5). Defaults to 3
   * when omitted. Used in the candidate aggregation:
   *   composite = Σ(criticality_k × product_score_k) / Σ criticality_k
   * The values below are the redlined baseline (load-bearing systems weighted
   * up). They are the catalogue DEFAULT — the owner can retune any component
   * live in the /candidates UI, which writes a `component_criticality` override
   * row (migration 0073) resolved by criticalityOf's `overrides` argument.
   */
  criticality?: 1 | 2 | 3 | 4 | 5;
};

export const DEFAULT_CRITICALITY: 1 | 2 | 3 | 4 | 5 = 3;

/**
 * Resolve a component's effective criticality: live override (from the
 * `component_criticality` table) → catalogue baseline → DEFAULT_CRITICALITY.
 */
export function criticalityOf(
  component: Pick<ArchitectureComponent, "id" | "criticality">,
  overrides?: Record<string, number>
): number {
  return overrides?.[component.id] ?? component.criticality ?? DEFAULT_CRITICALITY;
}

export const STAGE_LABEL: Record<Stage, string> = {
  b2c: "Current B2C",
  b2b: "Full B2B vision",
  mvp: "B2B MVP (day one)",
  "b2b-users": "B2B Users Journey",
};

/** Short editorial headline for the active stage (shown under the stage chip). */
export const STAGE_HEADLINE: Record<Stage, string> = {
  b2c: "Current-state diagram — systems view",
  b2b: "Full future capability layer for the B2B app",
  mvp: "Fastest-to-market B2B capability cut",
  "b2b-users": "Live co-edit — lean B2B end-to-end",
};

export const STAGE_DESCRIPTION: Record<Stage, string> = {
  b2c: "What you already operate today: the concrete platforms and integrations in production, grouped by domain so the footprint is legible. Here we talk in systems—each node is a real product in the live B2C stack.",
  b2b: "Emphasis on capabilities, not a system-for-system map: the functions and services you will need later to support the B2B experience. Several capabilities can roll up into a single platform or vendor; highlights show what the end-state operating model must cover.",
  mvp: "The smallest capability slice of that future surface required to launch B2B quickly—not every long-term platform decision. Multiple needs can ship inside one system; highlights mark the day-one launch line only.",
  "b2b-users":
    "Live editable canvas for the B2B users journey meeting. Drag nodes, drag-connect to draw arrows, double-click a label to rename, or use the toolbar to add components or actors. Everything persists; Reset returns to seed.",
};

// Positions below are seed defaults. Once a user opens /architecture/map, the
// workspace (architecture_workspace_state.map_nodes JSONB) becomes the source
// of truth and these literals are no longer consulted at runtime. To re-seed
// a fresh workspace, edit values here and call resetToSeed from the map UI.
//
// The seed used to be computed by a clustered grid layout; that math was
// dropped in 0031. Positions resolved from the prior layout are now hardcoded
// per node. Original cluster shape (documentation only):
//   row 0 (y=0):     Supply Chain (2×3) | Catalog/Content (2×1) | App Core (3×1)
//   row 1 (y=~470):  Pricing/Commerce (2×2) | Fulfillment/Ops (2×4) | Customer Experience (2×4)
//   row 2 (y=~1070): Finance (4×2 wide)            | Data/BI (3×2)

export const architectureComponents: readonly ArchitectureComponent[] = [
  // ─── Supply Chain (5) ────────────────────────────────────────────
  {
    id: "gs1",
    name: "GS1",
    role: "Global supplier product master (barcodes, images, attributes)",
    ownership: "external",
    domain: "supply-chain",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-06-pim-walkthrough"],
    position: { x: 20, y: 50 },
  },
  {
    id: "merlog",
    name: 'Merlog ("S1")',
    role: "Central warehouse — products land here first in the supply chain",
    criticality: 4,
    ownership: "external",
    domain: "supply-chain",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery", "2026-05-12-wms-operations-walkthrough"],
    position: { x: 260, y: 50 },
  },
  {
    id: "manual-spreadsheets",
    name: "Manual Spreadsheets",
    role: "Google Sheets / Excel — supplier onboarding, purchase-price mapa, shift plan",
    ownership: "external",
    domain: "supply-chain",
    stages: ["b2c", "b2b"],
    meetings: [
      "2026-05-05-b2b-supply-chain-discovery",
      "2026-05-06-pim-walkthrough",
      "2026-05-11-pricing-promotions-walkthrough",
      "2026-05-11-delivery-operations-walkthrough",
    ],
    position: { x: 20, y: 180 },
    note: "Manual workflow — not digital",
  },
  {
    id: "woody",
    name: "Woody (ERP)",
    role: "Supplier ordering, PO management — Yandex ERP",
    criticality: 4,
    ownership: "yandex",
    domain: "supply-chain",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery"],
    position: { x: 260, y: 180 },
  },
  {
    id: "woody-autoorder",
    name: "Woody Auto-Order",
    role: "Daily PO auto-generation from 3-week sales-weighted forecast",
    ownership: "yandex",
    domain: "supply-chain",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery", "2026-05-12-wms-operations-walkthrough"],
    position: { x: 20, y: 310 },
    note: "Manual in early B2B months per R19",
  },

  // ─── Catalog / Content (2) ───────────────────────────────────────
  {
    id: "yandex-pim",
    name: "Yandex PIM",
    role: "Product catalog — Grids → Groups → Categories → Products",
    criticality: 4,
    ownership: "yandex",
    domain: "catalog-content",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-06-pim-walkthrough", "2026-05-05-b2b-supply-chain-discovery"],
    position: { x: 540, y: 50 },
  },
  {
    id: "tanker-key",
    name: "Tanker Key",
    role: "Yandex translation-key store (i18n)",
    ownership: "yandex",
    domain: "catalog-content",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-06-pim-walkthrough"],
    position: { x: 780, y: 50 },
  },

  // ─── App Core (2) ────────────────────────────────────────────────
  {
    id: "yango-app",
    name: "Yango App",
    role: "Customer-facing iOS / Android app",
    criticality: 5,
    ownership: "yandex",
    domain: "app-core",
    stages: ["b2c", "b2b", "mvp"],
    meetings: [
      "2026-05-12-communication-system-walkthrough",
      "2026-05-11-pricing-promotions-walkthrough",
    ],
    position: { x: 1060, y: 50 },
  },
  // ─── Pricing / Commerce (3) ──────────────────────────────────────
  {
    id: "discounts-engine",
    name: "Discounts Engine",
    role: 'Pricing-rule conflict resolution ("sogar" priority logic)',
    ownership: "yandex",
    domain: "pricing-commerce",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-pricing-promotions-walkthrough"],
    position: { x: 20, y: 520 },
  },
  {
    id: "adminca-promotions",
    name: "Adminca — Promotions",
    role: "Promotion engine (section, handling, bundle, dynamic, markdown, basket, conditional)",
    criticality: 4,
    ownership: "yandex",
    domain: "pricing-commerce",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-11-pricing-promotions-walkthrough"],
    position: { x: 260, y: 520 },
  },
  {
    id: "adminca-coupons",
    name: "Adminca — Coupons",
    role: "Cart-level discount module (JSON admin UI; one coupon per order)",
    ownership: "yandex",
    domain: "pricing-commerce",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-pricing-promotions-walkthrough"],
    position: { x: 20, y: 650 },
  },

  // ─── Fulfillment / Ops (4 base nodes) ────────────────────────────
  {
    id: "wms",
    name: "WMS",
    role: "Warehouse management — inventory, picker tasks, dispatch, cycle counts",
    criticality: 5,
    ownership: "yandex",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b", "mvp"],
    meetings: [
      "2026-05-12-wms-operations-walkthrough",
      "2026-05-11-delivery-operations-walkthrough",
    ],
    position: { x: 540, y: 520 },
  },
  {
    id: "yandex-pro",
    name: "Yandex Pro",
    role: "Driver app — drivers see/claim shifts; orders auto-assigned by dispatch",
    criticality: 4,
    ownership: "yandex",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-delivery-operations-walkthrough"],
    position: { x: 780, y: 520 },
  },
  {
    id: "shift-organizer",
    name: "Shift Organizer",
    role: "Workforce scheduling — per-store shift plans",
    ownership: "external",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-delivery-operations-walkthrough"],
    position: { x: 540, y: 650 },
  },
  {
    id: "crm-go",
    name: "CRM GO",
    role: "Yandex driver-onboarding system",
    ownership: "yandex",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-delivery-operations-walkthrough"],
    position: { x: 780, y: 650 },
  },

  // ─── Customer Experience (4 base nodes) ──────────────────────────
  {
    id: "chatterbox",
    name: "Chatterbox",
    role: "Customer-support chat platform — Suggest → ML → live-agent routing",
    ownership: "yandex",
    domain: "customer-experience",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-06-customer-support-discovery"],
    position: { x: 1060, y: 520 },
  },
  {
    id: "tracker",
    name: "Tracker",
    role: "Yandex internal ticketing — form-fill → routed queue → email notification",
    ownership: "yandex",
    domain: "customer-experience",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-06-customer-support-discovery"],
    position: { x: 1300, y: 520 },
  },
  {
    id: "sms-gateway",
    name: "SMS Gateway",
    role: "Outbound SMS — Israeli vendor, VPN-routed workaround",
    ownership: "external",
    domain: "customer-experience",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-11-bi-analytics-walkthrough"],
    position: { x: 1060, y: 650 },
  },
  {
    id: "comm-system",
    name: "Communication System",
    role: "In-app CMS (Admin Panel for content + Adminca Experiments for targeting)",
    ownership: "yandex",
    domain: "customer-experience",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-12-communication-system-walkthrough"],
    position: { x: 1300, y: 650 },
  },

  // ─── Finance (4 base nodes) ──────────────────────────────────────
  {
    id: "yad",
    name: "Yad",
    role: "Israeli payment-services platform — clearing-and-issuing layer between app and accounting",
    criticality: 5,
    ownership: "external",
    domain: "finance",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-18-finance-walkthrough"],
    position: { x: 20, y: 1120 },
    note: "Avishai Srik (CPO) is Yango's integration counterparty",
  },
  {
    id: "trust",
    name: "Trust",
    role: "Clearing aggregator between Yad and EZCount (Hebrew transcription variant 'Trass')",
    ownership: "external",
    domain: "finance",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-18-finance-walkthrough"],
    position: { x: 260, y: 1120 },
    note: "Name + role pending confirmation with Avishai Srik (R83)",
  },
  {
    id: "ezcount",
    name: "EZCount",
    role: "Accounting integration (invoicing, card refunds, dual B2B model: per-order חשבונית מס קבלה or monthly consolidated)",
    criticality: 4,
    ownership: "external",
    domain: "finance",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-06-customer-support-discovery", "2026-05-18-finance-walkthrough"],
    position: { x: 500, y: 1120 },
  },
  {
    id: "obligo",
    name: "Obligo",
    role: "Post-payment / credit-limit governance for B2B",
    criticality: 4,
    ownership: "external",
    domain: "finance",
    stages: ["b2b", "mvp"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery", "2026-05-06-customer-support-discovery"],
    position: { x: 740, y: 1120 },
    note: "B2B-only — not used in current B2C (cash)",
  },
  {
    id: "priority",
    name: "Priority (ERP)",
    role: "ERP / ledger-of-record — receives end-of-period batches from EZCount; PnL aggregation",
    criticality: 4,
    ownership: "external",
    domain: "finance",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-18-finance-walkthrough"],
    position: { x: 20, y: 1250 },
    note: "Today Excel-bridge from EZCount; move to API named in R81",
  },

  // ─── Data / BI (4 base nodes) ────────────────────────────────────
  {
    id: "appmetrica",
    name: "AppMetrica",
    role: "Mobile event analytics — session-level pre-aggregates only",
    ownership: "yandex",
    domain: "data-bi",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-bi-analytics-walkthrough"],
    position: { x: 1020, y: 1120 },
  },
  {
    id: "datalens",
    name: "Datalens",
    role: "BI dashboarding — Yandex; ~100+ dashboards built in 4 years",
    ownership: "yandex",
    domain: "data-bi",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-bi-analytics-walkthrough"],
    position: { x: 1260, y: 1120 },
  },
  {
    id: "nirvana",
    name: "Nirvana",
    role: "SQL workflow orchestrator — daily PO generation, segmentation feeds",
    ownership: "yandex",
    domain: "data-bi",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-bi-analytics-walkthrough"],
    position: { x: 1500, y: 1120 },
  },
  {
    id: "segmentation-service",
    name: "Segmentation Service",
    role: "Customer-segment tagging — SQL predicates → phone-ID list, one-way",
    ownership: "yandex",
    domain: "data-bi",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-bi-analytics-walkthrough"],
    position: { x: 1020, y: 1250 },
  },
  // ─── App Core additions (B2B) ────────────────────────────────────
  {
    id: "b2b-mobile-app",
    name: "Yango Deli B2B App",
    role: "Customer-facing B2B mobile app — phone+OTP auth, admin/orderer roles, unit-or-carton ordering, in-stock-only catalog",
    ownership: "yandex",
    domain: "app-core",
    stages: ["mvp", "b2b", "b2b-users"],
    meetings: ["2026-05-18-b2b-users-discovery", "2026-05-18-finance-walkthrough"],
    position: { x: 1540, y: 50 },
  },

  // ─── Pricing / Commerce additions (B2B) ──────────────────────────
  {
    id: "b2b-pricing-engine",
    name: "B2B Pricing Engine",
    role: "Single standard price list + per-client end-of-invoice discount; segmented-catalog infrastructure built but unused at MVP",
    ownership: "yandex",
    domain: "pricing-commerce",
    stages: ["mvp", "b2b", "b2b-users"],
    meetings: ["2026-05-18-b2b-users-discovery"],
    position: { x: 260, y: 650 },
    note: "Segmented-catalog infra carries R80 stale-risk",
  },

  // ─── Fulfillment / Ops additions ─────────────────────────────────
  {
    id: "adminca-drivers",
    name: "Adminca — Drivers",
    role: "Driver-onboarding sub-module of Adminca — captures תעודת זהות, bank, אישור ניהול ספרים, אישור ניכוי מס במקור",
    ownership: "yandex",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-18-finance-walkthrough"],
    position: { x: 540, y: 780 },
  },
  {
    id: "driver-cabinet",
    name: "Driver Cabinet",
    role: "Couriers' per-period invoice + payment-status surface (paid / pending / amount)",
    ownership: "yandex",
    domain: "fulfillment-ops",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-18-finance-walkthrough"],
    position: { x: 780, y: 780 },
  },
  {
    id: "subcontractor-delivery",
    name: "3rd-party Refrigerated Subcontractor",
    role: "External refrigerated delivery partner for B2B — status-only visibility (shipped / delivered), ~4-hour windows",
    ownership: "external",
    domain: "fulfillment-ops",
    stages: ["mvp", "b2b", "b2b-users"],
    meetings: ["2026-05-18-b2b-users-discovery"],
    position: { x: 540, y: 910 },
    note: "Vendor unselected — R78 high/red",
  },

  // ─── Customer Experience additions ───────────────────────────────
  {
    id: "inforu",
    name: "Inforu",
    role: "WhatsApp customer-support integration platform — single unified B2B channel at MVP",
    ownership: "external",
    domain: "customer-experience",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-18-b2b-users-discovery"],
    position: { x: 1060, y: 780 },
  },
  {
    id: "b2b-onboarding-portal",
    name: "B2B Onboarding Portal",
    role: "Self-signup form + KYB document upload + manual review → Finance credit setup → EZCount activation",
    ownership: "yandex",
    domain: "customer-experience",
    stages: ["mvp", "b2b", "b2b-users"],
    meetings: ["2026-05-18-b2b-users-discovery", "2026-05-18-finance-walkthrough"],
    position: { x: 1300, y: 780 },
    note: "Approval-gate owner missing — R84",
  },
  {
    id: "b2b-customer-crm",
    name: "B2B Customer CRM",
    role: "Per-business customer record — admin user, ordering users, addresses, price list, discount %, payment terms, credit balance",
    ownership: "yandex",
    domain: "customer-experience",
    stages: ["mvp", "b2b", "b2b-users"],
    meetings: ["2026-05-18-b2b-users-discovery", "2026-05-18-finance-walkthrough"],
    position: { x: 1060, y: 910 },
    note: "יחוד עסקים modelling required for multi-ח.פ. clients — R85",
  },
];
